import React, {useEffect, useRef, useState} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {Link, useParams} from "react-router-dom";
import {baseUrl, ipAddress, userLocation, userName} from "../Univ.js";
function FixAppointment() {
    const {cons_id} = useParams();
    // =========================================================
    // CONSULTANT
    // =========================================================
    const [consData, setConsData] = useState(null);
    const [consultationFee, setConsultationFee] = useState("");
    const [consultantLoading, setConsultantLoading] = useState(true);
    // =========================================================
    // TABS
    // =========================================================
    const [activeTab, setActiveTab] = useState("revisit");
    // =========================================================
    // SEARCH
    // =========================================================
    const [searchTerm, setSearchTerm] = useState("");
    const [patients, setPatients] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [patientPhoto, setPatientPhoto] = useState(null);
    const [patientPhotoPreview, setPatientPhotoPreview] = useState("");
    const [cameraOpen, setCameraOpen] = useState(false);
    const [cameraError, setCameraError] = useState("");
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const cameraStreamRef = useRef(null);
    // =========================================================
    // SELECTED PATIENT
    // =========================================================
    const [selectedPatient, setSelectedPatient] = useState(null);
    // =========================================================
    // NEW PATIENT
    // =========================================================
    const [gender, setGender] = useState("male");
    const [registerFormData, setRegisterFormData] = useState({
        title: "Mr", patient: "", age: "", mobile: "", abha: "", address: "", refered: "walkin", refconsultant: ""
    });
    // =========================================================
    // APPOINTMENT
    // =========================================================
    const getCurrentDate = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };
    const getCurrentTime = () => {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        return `${hours}:${minutes}`;
    };
    const [appointmentDate, setAppointmentDate] = useState(getCurrentDate());
    const [appointmentTime, setAppointmentTime] = useState(getCurrentTime());
    // =========================================================
    // SUBMIT STATES
    // =========================================================
    const [saving, setSaving] = useState(false);
    // =========================================================
    // LOAD CONSULTANT
    // =========================================================
    useEffect(() => {
        let mounted = true;
        const loadConsultant = async () => {
            try {
                setConsultantLoading(true);
                const response = await axios.get(`${baseUrl}edit-consultant/${cons_id}`);
                if (mounted) {
                    setConsData(response.data);
                    setConsultationFee(response.data?.fees ?? 0);
                }
            } catch (error) {
                console.error("Consultant loading failed:", error);
                if (mounted) {
                    Swal.fire({
                        title: "Error", text: "Unable to load consultant details.", icon: "error"
                    });
                }
            } finally {
                if (mounted) {
                    setConsultantLoading(false);
                }
            }
        };
        if (cons_id) {
            loadConsultant();
        }
        return () => {
            mounted = false;
        };
    }, [cons_id]);
    // =========================================================
    // SEARCH INPUT
    // =========================================================
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };
// =========================================================
// SEARCH PATIENT
// =========================================================
    const searchPatient = async () => {
        const value = searchTerm.trim();
        if (!value) {
            Swal.fire({
                title: "Enter Patient Search",
                text: "Enter Patient ID, UPID, patient name, mobile number or ABHA.",
                icon: "warning"
            });
            return;
        }
        try {
            setSearchLoading(true);
            setSearchPerformed(false);
            setPatients([]);
            setSelectedPatient(null);
            const response = await axios.post(`${baseUrl}patient-identity/search/`, {
                search: value
            }, {
                params: {
                    location: userLocation
                }
            });
            console.log("Patient search response:", response.data);
            // =====================================================
            // BACKEND RETURNS:
            //
            // {
            //     success: true,
            //     found: true,
            //     patients: [ ...multiple patients... ],
            //     patient: { ...single patient... }
            // }
            //
            // IMPORTANT:
            // Use `patients` for the search results.
            // Do NOT use `patient` here.
            // =====================================================
            if (response.data.success && Array.isArray(response.data.patients)) {
                setPatients(response.data.patients);
            } else {
                setPatients([]);
            }
            setSearchPerformed(true);
        } catch (error) {
            console.error("Patient search failed:", error);
            setPatients([]);
            setSearchPerformed(true);
            Swal.fire({
                title: "Search Error", text: error.response?.data?.message || "Unable to search patient.", icon: "error"
            });
        } finally {
            setSearchLoading(false);
        }
    };
    const startCamera = async () => {
        try {
            setCameraError("");
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                setCameraError("Camera access is not supported by this browser.");
                return;
            }
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: "user", width: {
                        ideal: 1280
                    }, height: {
                        ideal: 720
                    }
                }, audio: false
            });
            cameraStreamRef.current = stream;
            setCameraOpen(true);
            // Wait until video element is rendered
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play().catch(() => {
                    });
                }
            }, 100);
        } catch (error) {
            console.error("Camera error:", error);
            let message = "Unable to access camera.";
            if (error.name === "NotAllowedError") {
                message = "Camera permission was denied. Please allow camera access in your browser.";
            } else if (error.name === "NotFoundError") {
                message = "No camera was found on this device.";
            } else if (error.name === "NotReadableError") {
                message = "Camera is already being used by another application.";
            }
            setCameraError(message);
        }
    };
    const capturePatientPhoto = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) {
            return;
        }
        const width = video.videoWidth;
        const height = video.videoHeight;
        if (!width || !height) {
            Swal.fire({
                title: "Camera Not Ready", text: "Please wait for the camera preview.", icon: "warning"
            });
            return;
        }
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d");
        context.drawImage(video, 0, 0, width, height);
        canvas.toBlob((blob) => {
            if (!blob) {
                return;
            }
            const file = new File([blob], `patient_${Date.now()}.jpg`, {
                type: "image/jpeg"
            });
            setPatientPhoto(file);
            const previewUrl = URL.createObjectURL(blob);
            setPatientPhotoPreview(previewUrl);
            stopCamera();
        }, "image/jpeg", 0.90);
    };
    const stopCamera = () => {
        if (cameraStreamRef.current) {
            cameraStreamRef.current
                .getTracks()
                .forEach(track => track.stop());
            cameraStreamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setCameraOpen(false);
    };
    useEffect(() => {
        return () => {
            if (cameraStreamRef.current) {
                cameraStreamRef.current
                    .getTracks()
                    .forEach(track => track.stop());
            }
        };
    }, []);
    // =========================================================
    // SEARCH ENTER
    // =========================================================
    const handleSearchKeyDown = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            searchPatient();
        }
    };
    // =========================================================
    // SELECT PATIENT
    // =========================================================
    const selectPatient = (patient) => {
        setSelectedPatient(patient);
        Swal.fire({
            title: "Patient Selected",
            text: patient.patient || patient.full_name || patient.name || "Patient selected successfully.",
            icon: "success",
            timer: 1200,
            showConfirmButton: false
        });
    };
    // =========================================================
    // NEW PATIENT INPUT
    // =========================================================
    const handleRegisterInput = (event) => {
        const {
            name, value
        } = event.target;
        setRegisterFormData(previous => ({
            ...previous, [name]: value
        }));
    };
    // =========================================================
    // GENDER
    // =========================================================
    const handleGenderChange = (event) => {
        setGender(event.target.value);
    };
    const createAppointment = async (patient, appointmentType) => {
        if (!patient) {
            Swal.fire({
                title: "Patient Required", text: "Please select a patient.", icon: "warning"
            });
            return;
        }
        if (!appointmentDate) {
            Swal.fire({
                title: "Date Required", text: "Please select appointment date.", icon: "warning"
            });
            return;
        }
        if (!appointmentTime) {
            Swal.fire({
                title: "Time Required", text: "Please select appointment time.", icon: "warning"
            });
            return;
        }
        try {
            setSaving(true);
            // =================================================
            // 1. GET LOCAL PATIENT / LINK MASTER IDENTITY
            // =================================================
            const formData = new FormData();
            formData.append("upid", patient.upid || patient.patient_upid || "");
            formData.append("patient_id", patient.patient_id || patient.pat_id || "");
            formData.append("pat_id", patient.pat_id || patient.patient_id || "");
            formData.append("patient_pk", patient.patient_pk || patient.id || "");
            formData.append("title", patient.title || patient.my_title || registerFormData.title || "Mr");
            formData.append("patient", patient.patient || "");
            formData.append("age", patient.age || "");
            formData.append("gender", patient.gender || gender || "");
            formData.append("mobile", patient.mobile || "");
            formData.append("abha", patient.abha || "");
            formData.append("address", patient.address || patient.address1 || "");
            formData.append("refered", patient.refered || registerFormData.refered || "walkin");
            formData.append("refconsultant", patient.refconsultant || registerFormData.refconsultant || "");
            formData.append("addedby", userName || "SYSTEM");
            formData.append("ip_add", ipAddress);
            formData.append("location", userLocation);
// NEW PATIENT PHOTO
            if (appointmentType === "new" && patientPhoto) {
                formData.append("profile_pic", patientPhoto);
            }
            const linkResponse = await axios.post(`${baseUrl}patient/register-or-link/`, formData, {
                params: {
                    location: userLocation
                }
            });
            console.log("Register / Link response:", linkResponse.data);
            const linkedPatient = linkResponse.data;
            // =================================================
            // 2. DETERMINE LOCAL PATIENT ID
            // =================================================
            const localPatientId = linkedPatient.patient_pk || linkedPatient.patient_id || linkedPatient.pat_id || linkedPatient.local_patient_id || patient.patient_pk || patient.id || patient.patient_id || patient.pat_id;
            if (!localPatientId) {
                throw new Error("Local patient ID was not returned by patient/register-or-link/.");
            }
            // =================================================
            // 3. CREATE OP APPOINTMENT
            // =================================================
            const appointmentResponse = await axios.post(`${baseUrl}create-op-appointment/`, {
                patient_id: localPatientId,
                pat_id: localPatientId,
                cons_id: cons_id,
                appointment_type: appointmentType,
                type: appointmentType,
                appt_date: appointmentDate,
                appt_time: appointmentTime,
                refered: patient.refered || registerFormData.refered || "walkin",
                addedby: userName || "SYSTEM",
                ip_add: ipAddress,
                location: userLocation
            });
            console.log("Appointment response:", appointmentResponse.data);
            // =================================================
            // ONLY CONTINUE IF APPOINTMENT CREATED
            // =================================================
            if (appointmentResponse.status !== 200 && appointmentResponse.status !== 201) {
                throw new Error("Appointment was not created.");
            }
            const appointment = appointmentResponse.data;
            const token = appointment.token || appointment.tokid || appointment.token_number || "";
            const appointmentId = appointment.id || appointment.appointment_id || appointment.appt_id || "";
            const billappt = appointment.appt_id;
            if (!appointmentId) {
                throw new Error("Appointment created but appointment ID was not returned.");
            }
            // =================================================
            // 4. CREATE OP BILL
            // =================================================
            // =================================================
// 4. CREATE OP BILL
// =================================================
            console.log("Creating OP Bill...");
            const consultationFeeValue = Number(consultationFee) || 0;
// NEW PATIENT:
// Consultation Fee + ₹50 Registration Fee
//
// REVISIT:
// Consultation Fee only
            const opBillFee = appointmentType === "new" ? consultationFeeValue + 50 : consultationFeeValue;
            console.log("Appointment Type:", appointmentType);
            console.log("Edited Consultation Fee:", consultationFeeValue);
            console.log("OP Bill Fee:", opBillFee);
            const opBillResponse = await axios.post(`${baseUrl}op-bill-create/`, {
                appt_id: billappt,
                pat: localPatientId,
                cons: cons_id,
                fee: opBillFee,
                appointment_type: appointmentType,
                status: "1",
                addedby: userName || "SYSTEM",
                ip_add: ipAddress,
                location: userLocation
            });
            console.log("OP Bill response:", opBillResponse.data);
            // =================================================
            // OP BILL SUCCESS CHECK
            // =================================================
            if (opBillResponse.status !== 200 && opBillResponse.status !== 201) {
                throw new Error("Appointment was created, but OP Bill creation failed.");
            }
            // =================================================
            // 5. SUCCESS
            // =================================================
            Swal.fire({
                title: appointmentType === "revisit" ? "Appointment Fixed Successfully" : "Patient Registered Successfully",
                html: `

                <div style="text-align:left">

                    ${linkedPatient.upid ? `
                                <div style="margin-bottom:8px">
                                    <strong>DrHiS UPID:</strong>
                                    ${linkedPatient.upid}
                                </div>
                              ` : ""}


                    ${linkedPatient.patient_id ? `
                                <div style="margin-bottom:8px">
                                    <strong>Patient ID:</strong>
                                    ${linkedPatient.patient_id}
                                </div>
                              ` : ""}


                    ${token ? `
                                <div style="margin-bottom:8px">
                                    <strong>Token Number:</strong>
                                    ${token}
                                </div>
                              ` : ""}


                    <div style="margin-bottom:8px">
                        <strong>OP Bill:</strong>
                        Created Successfully
                    </div>

                </div>

            `,
                icon: "success",
                timer: 3500,
                showConfirmButton: false
            });
            // =================================================
            // 6. TOKEN VIEW
            // =================================================
            setTimeout(() => {
                window.location.href = `/token-view/${appointmentId}/`;
            }, 3500);
        } catch (error) {
            console.error("Appointment / OP Bill creation failed:", error);
            let message = "Unable to create appointment.";
            if (error.response && error.response.data) {
                const data = error.response.data;
                if (data.error) {
                    message = data.error;
                } else if (data.message) {
                    message = data.message;
                } else if (data.detail) {
                    message = data.detail;
                } else {
                    try {
                        message = JSON.stringify(data);
                    } catch {
                        message = "Server error.";
                    }
                }
            } else if (error.message) {
                message = error.message;
            }
            Swal.fire({
                title: "Appointment Error", text: message, icon: "error"
            });
        } finally {
            setSaving(false);
        }
    };
    // =========================================================
    // REVISIT
    // =========================================================
    const handleRevisitPatient = async (patient) => {
        if (!patient) {
            return;
        }
        setSelectedPatient(patient);
        const result = await Swal.fire({
            title: "Fix Revisit Appointment?",
            html: `
            <div style="text-align:left">
                <div class="mb-2">
                    <strong>Patient:</strong>
                    ${patient.patient || "-"}
                </div>

                <div class="mb-2">
                    <strong>Patient ID:</strong>
                    ${patient.pat_id || "-"}
                </div>

                <div class="mb-2">
                    <strong>Mobile:</strong>
                    ${patient.mobile || "-"}
                </div>

                <div class="mb-2">
                    <strong>Consultant:</strong>
                    ${consData?.consultant || consData?.name || "-"}
                </div>

                <div class="mb-2">
    <strong>Consultation Fee:</strong>
    ₹${Number(consultationFee) || 0}
</div>
            </div>
        `,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, Fix Appointment",
            cancelButtonText: "Cancel"
        });
        if (result.isConfirmed) {
            createAppointment(patient, "revisit");
        }
    };
    const handleRevisit = () => {
        if (!selectedPatient) {
            Swal.fire({
                title: "Select Patient", text: "Please select a patient from the search results.", icon: "warning"
            });
            return;
        }
        Swal.fire({
            title: "Confirm Appointment",
            html: `

                <div>

                    Fix a revisit appointment for

                    <strong>

                        ${selectedPatient.patient || selectedPatient.full_name || selectedPatient.name || "this patient"}

                    </strong>?

                </div>

            `,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, Fix Appointment",
            cancelButtonText: "Cancel"
        }).then(result => {
            if (result.isConfirmed) {
                createAppointment(selectedPatient, "revisit");
            }
        });
    };
    // =========================================================
    // NEW PATIENT
    // =========================================================
    const handleNewPatient = async () => {
        if (!registerFormData.patient.trim()) {
            Swal.fire({
                title: "Patient Name Required", icon: "warning"
            });
            return;
        }
        if (!registerFormData.age) {
            Swal.fire({
                title: "Age Required", icon: "warning"
            });
            return;
        }
        if (!registerFormData.mobile) {
            Swal.fire({
                title: "Mobile Number Required", icon: "warning"
            });
            return;
        }
        const patient = {
            title: registerFormData.title,
            patient: registerFormData.patient,
            age: registerFormData.age,
            gender: gender,
            mobile: registerFormData.mobile,
            abha: registerFormData.abha,
            address: registerFormData.address,
            refered: registerFormData.refered,
            refconsultant: registerFormData.refconsultant
        };
        const confirmed = await Swal.fire({
            title: "Register Patient?",
            html: `

                    <div style="text-align:left">

                        <div>
                            <strong>Name:</strong>
                            ${patient.patient}
                        </div>

                        <div>
                            <strong>Age:</strong>
                            ${patient.age}
                        </div>

                        <div>
                            <strong>Mobile:</strong>
                            ${patient.mobile}
                        </div>

                    </div>

                `,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Register & Fix Appointment",
            cancelButtonText: "Cancel"
        });
        if (!confirmed.isConfirmed) {
            return;
        }
        await createAppointment(patient, "new");
    };
    const newPatientValid = registerFormData.patient.trim() !== "" && registerFormData.age !== "" && registerFormData.mobile !== "";
    // =========================================================
    // RENDER
    // =========================================================
    return (<section
        className="container-fluid mt-4"
    >

        {/* =================================================
                HEADER
            ================================================= */}

<div className="op-appointment-header">

    <div className="op-appointment-title">
        <div className="op-appointment-icon">
            <i className="fa-solid fa-calendar-check"></i>
        </div>

        <div>
            <h2>OP Appointment</h2>
            <p>Outpatient Registration & Consultation</p>
        </div>
    </div>

    {consData && (
        <div className="op-consultant-panel">

            <div className="op-consultant-info">
                <span className="op-section-label">
                    CONSULTANT
                </span>

                <div className="op-consultant-name">
                    <i className="fa-solid fa-user-doctor"></i>

                    <strong>
                        {consData.consultant ||
                         consData.name ||
                         consData.doctor ||
                         ""}
                    </strong>
                </div>
            </div>

            {consData?.fees !== undefined && (
                <div className="op-fee-panel">
                    <label htmlFor="consultation-fee">
                        Consultation Fee
                    </label>

                    <div className="op-fee-input-wrap">
                        <span>₹</span>

                        <input
                            id="consultation-fee"
                            type="number"
                            className="op-fee-input"
                            value={consultationFee}
                            min="0"
                            step="1"
                            onChange={(e) =>
                                setConsultationFee(e.target.value)
                            }
                        />
                    </div>
                </div>
            )}

        </div>
    )}

</div>



        {/* =================================================
                LOADING CONSULTANT
            ================================================= */}

        {consultantLoading && (<div className="alert alert-info">

            <i
                className="
                            fa-solid
                            fa-spinner
                            fa-spin
                            me-2
                        "
            ></i>

            Loading consultant details...

        </div>)}


        {/* =================================================
                TAB BUTTONS
            ================================================= */}

        <div className="appointment-tabs mb-4">

            <div
                className="
                        d-flex
                        gap-2
                        flex-wrap
                    "
            >

                <button

                    type="button"

                    className={`nav-link ${activeTab === "revisit" ? "active" : ""}`}

                    onClick={() => setActiveTab("revisit")}

                >

                    <i
                        className="
                                fa-solid
                                fa-rotate-left
                                me-2
                            "
                    ></i>

                    Revisit Patient

                </button>


                <button

                    type="button"

                    className={`nav-link ${activeTab === "new" ? "active" : ""}`}

                    onClick={() => setActiveTab("new")}

                >

                    <i
                        className="
                                fa-solid
                                fa-user-plus
                                me-2
                            "
                    ></i>

                    New Patient

                </button>

            </div>

        </div>


        {/* =================================================
                REVISIT
            ================================================= */}

        {activeTab === "revisit" && (<div>

            <div className="card">

                <div className="card-header">

                    <i
                        className="
                                    fa-solid
                                    fa-magnifying-glass
                                    me-2
                                "
                    ></i>

                    Search Existing Patient

                </div>


                <div className="card-body">

                    <div className="row align-items-end">

                        <div className="col-lg-9">

                            <label
                                className="form-label"
                            >

                                Patient Search

                            </label>


                            <div className="input-group">

                                        <span
                                            className="
                                                input-group-text
                                            "
                                        >

                                            <i
                                                className="
                                                    fa-solid
                                                    fa-id-card
                                                "
                                            ></i>

                                        </span>


                                <input

                                    type="text"

                                    className="
                                                form-control
                                            "

                                    placeholder="UPID / Patient ID / Patient Name / Mobile / ABHA"
                                    value={searchTerm}

                                    onChange={handleSearchChange}

                                    onKeyDown={handleSearchKeyDown}

                                />

                            </div>


                            <small
                                className="
                                            text-muted
                                            mt-2
                                            d-block
                                        "
                            >

                                Search using DrHiS UPID,
                                local Patient ID,
                                mobile number or ABHA.

                            </small>

                        </div>


                        <div className="col-lg-3 mt-3 mt-lg-0">

                            <button

                                type="button"

                                className="
                                            btn
                                            btn-primary
                                            w-100
                                        "

                                onClick={searchPatient}

                                disabled={searchLoading}

                            >

                                {searchLoading ? (<>

                                    <i
                                        className="
                                                        fa-solid
                                                        fa-spinner
                                                        fa-spin
                                                        me-2
                                                    "
                                    ></i>

                                    Searching...

                                </>) : (<>

                                    <i
                                        className="
                                                        fa-solid
                                                        fa-magnifying-glass
                                                        me-2
                                                    "
                                    ></i>

                                    Search

                                </>)}

                            </button>

                        </div>

                    </div>

                </div>

            </div>


            {/* =================================================
    SEARCH RESULTS
================================================= */}

            {searchPerformed && (<div className="card mt-4">

                {/* =================================================
            HEADER
        ================================================= */}

                <div className="card-header d-flex align-items-center">

                    <i className="fa-solid fa-users me-2"></i>

                    <span>Patient Search Results</span>

                    {patients.length > 0 && (<span className="badge bg-primary ms-2">
                    {patients.length}
                </span>)}

                </div>


                {/* =================================================
            BODY
        ================================================= */}

                <div className="card-body">

                    {patients.length === 0 ? (/* =================================================
                               NO RESULTS
                            ================================================= */

                        <div className="text-center py-5 text-muted">

                            <i className="fa-solid fa-user-slash fa-2x mb-3"></i>

                            <div className="fw-semibold">
                                No matching patient found.
                            </div>

                            <small>
                                If this is a new patient, use the New Patient tab.
                            </small>

                        </div>) : (/* =================================================
                               RESULTS TABLE
                            ================================================= */

                        <div className="table-responsive">

                            <table className="table-erp-modern">

                                <thead>

                                <tr>

                                    <th>UPID</th>

                                    <th>Patient ID</th>

                                    <th>Patient</th>

                                    <th>Gender</th>

                                    <th>Age</th>

                                    <th>Mobile</th>

                                    <th>ABHA</th>

                                    <th>Action</th>

                                </tr>

                                </thead>


                                <tbody>

                                {patients.map((patient, index) => {
                                    const rowId = patient.id ?? patient.pat_id ?? patient.upid ?? `patient-${index}`;
                                    const upid = patient.upid || "-";
                                    const patientId = patient.pat_id || "-";
                                    const patientName = patient.patient || patient.full_name || patient.name || "-";
                                    const gender = patient.gender || "-";
                                    const age = patient.age || "-";
                                    const mobile = patient.mobile || patient.mobile_no || patient.phone || "-";
                                    const abha = patient.abha || patient.abha_number || "-";
                                    /*
                                     * Check whether this row is currently selected.
                                     */
                                    const isSelected = selectedPatient && ((patient.id && selectedPatient.id === patient.id) || (patient.pat_id && selectedPatient.pat_id === patient.pat_id) || (patient.upid && selectedPatient.upid === patient.upid));
                                    return (<tr
                                        key={rowId}
                                        className={isSelected ? "table-success" : ""}
                                    >

                                        {/* =================================================
                                            UPID
                                        ================================================= */}

                                        <td>

                                            {upid !== "-" ? (<Link
                                                to={`/emr-details/${patientId !== "-" ? patientId : upid}`}
                                                className="fw-bold"
                                            >
                                                {upid}
                                            </Link>) : ("-")}

                                        </td>


                                        {/* =================================================
                                            PATIENT ID
                                        ================================================= */}

                                        <td>
                                        <Link to={`/edit-patient/${patient.id}`}
                                          target="_blank">{patientId}</Link>

                                        </td>


                                        {/* =================================================
                                            PATIENT
                                        ================================================= */}

                                        <td>

                                            <strong>
                                                {patientName}
                                            </strong>

                                        </td>


                                        {/* =================================================
                                            GENDER
                                        ================================================= */}

                                        <td>
                                            {gender}
                                        </td>


                                        {/* =================================================
                                            AGE
                                        ================================================= */}

                                        <td>
                                            {age}
                                        </td>


                                        {/* =================================================
                                            MOBILE
                                        ================================================= */}

                                        <td>
                                            {mobile}
                                        </td>


                                        {/* =================================================
                                            ABHA
                                        ================================================= */}

                                        <td>
                                            {abha}
                                        </td>


                                        {/* =================================================
                                            ACTION
                                        ================================================= */}

                                        <td>

                                            <button
                                                type="button"
                                                className="btn btn-success btn-sm"
                                                disabled={saving}
                                                onClick={() => handleRevisitPatient(patient)}
                                            >

                                                <i className="fa-solid fa-calendar-plus me-1"></i>

                                                Fix Revisit Appointment

                                            </button>

                                        </td>

                                    </tr>);
                                })}

                                </tbody>

                            </table>

                        </div>)}

                </div>

            </div>)}

            {/* =================================================
                        SELECTED PATIENT
                    ================================================= */}

            {selectedPatient && (<div
                className="
                                card
                                mt-4
                                border-success
                            "
            >

                <div
                    className="
                                    card-header
                                    bg-success
                                    text-white
                                "
                >

                    <i
                        className="
                                        fa-solid
                                        fa-circle-check
                                        me-2
                                    "
                    ></i>

                    Selected Patient

                </div>


                <div className="card-body">

                    <div className="row">

                        <div className="col-lg-3">

                            <small
                                className="text-muted"
                            >

                                UPID

                            </small>

                            <div className="fw-bold">

                                {selectedPatient.upid || "-"}

                            </div>

                        </div>


                        <div className="col-lg-3">

                            <small
                                className="text-muted"
                            >

                                Patient ID

                            </small>

                            <div className="fw-bold">

                                {selectedPatient.patient_id || selectedPatient.pat_id || "-"}

                            </div>

                        </div>


                        <div className="col-lg-3">

                            <small
                                className="text-muted"
                            >

                                Patient

                            </small>

                            <div className="fw-bold">

                                {selectedPatient.patient || selectedPatient.full_name || "-"}

                            </div>

                        </div>


                        <div className="col-lg-3">

                            <small
                                className="text-muted"
                            >

                                Mobile

                            </small>

                            <div className="fw-bold">

                                {selectedPatient.mobile || "-"}

                            </div>

                        </div>

                    </div>

                </div>

            </div>)}


            {/* =================================================
                        APPOINTMENT
                    ================================================= */}

            <div className="card mt-4">

                <div className="card-header">

                    <i
                        className="
                                    fa-solid
                                    fa-calendar-days
                                    me-2
                                "
                    ></i>

                    Appointment Schedule

                </div>


                <div className="card-body">

                    <div className="row">

                        <div className="col-lg-6 mb-3">

                            <label
                                className="form-label"
                            >

                                Appointment Date

                            </label>


                            <input

                                type="date"

                                className="
                                            form-control
                                        "

                                value={appointmentDate}

                                onChange={event => setAppointmentDate(event.target.value)}

                            />

                        </div>


                        <div className="col-lg-6 mb-3">

                            <label
                                className="form-label"
                            >

                                Appointment Time

                            </label>


                            <input

                                type="time"

                                className="
                                            form-control
                                        "

                                value={appointmentTime}

                                onChange={event => setAppointmentTime(event.target.value)}

                            />

                        </div>

                    </div>


                    <div className="text-end mt-3">

                        <button

                            type="button"

                            className="
                                        btn
                                        btn-success
                                        px-5
                                    "

                            disabled={!selectedPatient || saving}

                            onClick={handleRevisit}

                        >

                            {saving ? (<>

                                <i
                                    className="
                                                    fa-solid
                                                    fa-spinner
                                                    fa-spin
                                                    me-2
                                                "
                                ></i>

                                Processing...

                            </>) : (<>

                                <i
                                    className="
                                                    fa-solid
                                                    fa-calendar-plus
                                                    me-2
                                                "
                                ></i>

                                Fix Revisit Appointment

                            </>)}

                        </button>

                    </div>

                </div>

            </div>

        </div>)}


        {/* =================================================
                NEW PATIENT
            ================================================= */}

        {activeTab === "new" && (<div className="card">

            <div className="card-header">

                <i
                    className="
                                fa-solid
                                fa-user-plus
                                me-2
                            "
                ></i>

                New Patient Registration

            </div>


            <div className="card-body">


                {/* =================================================
                            BASIC INFORMATION
                        ================================================= */}

                <div className="erp-form-section">

                    <i
                        className="
                                    fa-solid
                                    fa-id-card
                                    me-2
                                "
                    ></i>

                    Basic Information

                </div>


                <div className="row">
                    <div className="col-lg-12 mb-4">

                        <label className="form-label">
                            Patient Photo
                        </label>

                        <div className="d-flex align-items-center gap-3 flex-wrap">

                            {patientPhotoPreview && (<div>
                                <img
                                    src={patientPhotoPreview}
                                    alt="Patient"
                                    style={{
                                        width: "120px",
                                        height: "120px",
                                        objectFit: "cover",
                                        borderRadius: "10px",
                                        border: "1px solid #ddd"
                                    }}
                                />
                            </div>)}

                            <div>
                                <div className="d-flex align-items-center gap-3 flex-wrap">

                                    {patientPhotoPreview && (<div>
                                        <img
                                            src={patientPhotoPreview}
                                            alt="Patient"
                                            style={{
                                                width: "120px",
                                                height: "120px",
                                                objectFit: "cover",
                                                borderRadius: "10px",
                                                border: "1px solid #ddd"
                                            }}
                                        />
                                    </div>)}

                                    <div>

                                        <button
                                            type="button"
                                            className="btn btn-outline-primary"
                                            onClick={startCamera}
                                            disabled={cameraOpen}
                                        >
                                            <i className="fa-solid fa-camera me-2"></i>
                                            Take Patient Photo
                                        </button>

                                        {patientPhoto && (<div className="small text-success mt-2">
                                            <i className="fa-solid fa-check me-1"></i>
                                            Photo captured
                                        </div>)}

                                    </div>

                                </div>
                                {cameraOpen && (<div
                                    className="modal d-block"
                                    tabIndex="-1"
                                    style={{
                                        backgroundColor: "rgba(0,0,0,0.75)"
                                    }}
                                >

                                    <div className="modal-dialog modal-lg modal-dialog-centered">

                                        <div className="modal-content">

                                            <div className="modal-header">

                                                <h5 className="modal-title">
                                                    <i className="fa-solid fa-camera me-2"></i>
                                                    Patient Photo
                                                </h5>

                                                <button
                                                    type="button"
                                                    className="btn-close"
                                                    onClick={stopCamera}
                                                ></button>

                                            </div>

                                            <div className="modal-body text-center">

                                                {cameraError ? (<div className="alert alert-danger">
                                                    <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                                    {cameraError}
                                                </div>) : (<video
                                                    ref={videoRef}
                                                    autoPlay
                                                    playsInline
                                                    muted
                                                    style={{
                                                        width: "100%",
                                                        maxHeight: "500px",
                                                        objectFit: "cover",
                                                        borderRadius: "12px",
                                                        background: "#000"
                                                    }}
                                                />)}

                                                <canvas
                                                    ref={canvasRef}
                                                    style={{
                                                        display: "none"
                                                    }}
                                                />

                                            </div>

                                            <div className="modal-footer">

                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={stopCamera}
                                                >
                                                    <i className="fa-solid fa-xmark me-2"></i>
                                                    Cancel
                                                </button>

                                                {!cameraError && (<button
                                                    type="button"
                                                    className="btn btn-success px-4"
                                                    onClick={capturePatientPhoto}
                                                >
                                                    <i className="fa-solid fa-camera me-2"></i>
                                                    Capture Photo
                                                </button>)}

                                            </div>

                                        </div>

                                    </div>

                                </div>)}
                            </div>

                        </div>

                    </div>
                    <div className="col-lg-2 mb-4">

                        <label
                            className="form-label"
                        >

                            Title

                        </label>


                        <select

                            className="
                                        form-select
                                    "

                            name="title"

                            value={registerFormData.title}

                            onChange={handleRegisterInput}

                        >

                            <option value="Mr">
                                Mr
                            </option>

                            <option value="Mrs">
                                Mrs
                            </option>

                            <option value="Master">
                                Master
                            </option>

                            <option value="Baby">
                                Baby
                            </option>

                            <option value="Ms">
                                Ms
                            </option>

                            <option value="Dr">
                                Dr
                            </option>

                        </select>

                    </div>


                    <div className="col-lg-10 mb-4">

                        <label
                            className="form-label"
                        >

                            Patient Name

                        </label>


                        <input

                            type="text"

                            className="
                                        form-control
                                    "

                            name="patient"

                            placeholder="
                                        Enter Patient Name
                                    "

                            value={registerFormData.patient}

                            onChange={handleRegisterInput}

                        />

                    </div>

                </div>


                {/* =================================================
                            CONTACT
                        ================================================= */}

                <div
                    className="
                                erp-form-section
                                mt-4
                            "
                >

                    <i
                        className="
                                    fa-solid
                                    fa-address-book
                                    me-2
                                "
                    ></i>

                    Contact Information

                </div>


                <div className="row">

                    <div className="col-lg-3 mb-4">

                        <label
                            className="form-label"
                        >

                            Age

                        </label>


                        <input

                            type="number"

                            className="
                                        form-control
                                    "

                            name="age"

                            placeholder="Age"

                            value={registerFormData.age}

                            onChange={handleRegisterInput}

                            min="0"

                        />

                    </div>


                    <div className="col-lg-3 mb-4">

                        <label
                            className="form-label"
                        >

                            Mobile Number

                        </label>


                        <input

                            type="tel"

                            className="
                                        form-control
                                    "

                            name="mobile"

                            placeholder="
                                        Mobile Number
                                    "

                            value={registerFormData.mobile}

                            onChange={handleRegisterInput}

                            maxLength="10"

                        />

                    </div>


                    <div className="col-lg-6 mb-4">

                        <label
                            className="form-label"
                        >

                            ABHA

                        </label>


                        <input

                            type="text"

                            className="
                                        form-control
                                    "

                            name="abha"

                            placeholder="
                                        ABHA Number / ABHA Address
                                    "

                            value={registerFormData.abha}

                            onChange={handleRegisterInput}

                        />

                    </div>


                    <div className="col-lg-12 mb-4">

                        <label
                            className="form-label"
                        >

                            Address

                        </label>


                        <input

                            type="text"

                            className="
                                        form-control
                                    "

                            name="address"

                            placeholder="
                                        Patient Address
                                    "

                            value={registerFormData.address}

                            onChange={handleRegisterInput}

                        />

                    </div>

                </div>


                {/* =================================================
                            GENDER
                        ================================================= */}

                <div
                    className="
                                erp-form-section
                                mt-4
                            "
                >

                    <i
                        className="
                                    fa-solid
                                    fa-venus-mars
                                    me-2
                                "
                    ></i>

                    Gender

                </div>


                <div
                    className="
                                gender-group
                                mb-4
                            "
                >

                    <label
                        className={`
                                    gender-chip
                                    ${gender === "male" ? "active" : ""}
                                    `}
                    >

                        <input

                            type="radio"

                            name="gender"

                            value="male"

                            checked={gender === "male"}

                            onChange={handleGenderChange}

                        />

                        <i
                            className="
                                        fa-solid
                                        fa-mars
                                        me-2
                                    "
                        ></i>

                        Male

                    </label>


                    <label
                        className={`
                                    gender-chip
                                    ${gender === "female" ? "active" : ""}
                                    `}
                    >

                        <input

                            type="radio"

                            name="gender"

                            value="female"

                            checked={gender === "female"}

                            onChange={handleGenderChange}

                        />

                        <i
                            className="
                                        fa-solid
                                        fa-venus
                                        me-2
                                    "
                        ></i>

                        Female

                    </label>


                    <label
                        className={`
                                    gender-chip
                                    ${gender === "transgender" ? "active" : ""}
                                    `}
                    >

                        <input

                            type="radio"

                            name="gender"

                            value="transgender"

                            checked={gender === "transgender"}

                            onChange={handleGenderChange}

                        />

                        <i
                            className="
                                        fa-solid
                                        fa-transgender
                                        me-2
                                    "
                        ></i>

                        Transgender

                    </label>

                </div>


                {/* =================================================
                            REFERRAL
                        ================================================= */}

                <div
                    className="
                                erp-form-section
                                mt-4
                            "
                >

                    <i
                        className="
                                    fa-solid
                                    fa-share-nodes
                                    me-2
                                "
                    ></i>

                    Referral Information

                </div>


                <div className="row">

                    <div className="col-lg-5 mb-4">

                        <label
                            className="form-label"
                        >

                            Referred By

                        </label>


                        <select

                            className="
                                        form-select
                                    "

                            name="refered"

                            value={registerFormData.refered}

                            onChange={handleRegisterInput}

                        >

                            <option value="walkin">
                                Walk-in
                            </option>

                            <option value="medicalcamp">
                                Medical Camp
                            </option>

                            <option value="friends">
                                Friends
                            </option>

                            <option value="doctor">
                                Doctor
                            </option>

                            <option value="paperad">
                                Paper Advertisement
                            </option>

                            <option value="others">
                                Others
                            </option>

                        </select>

                    </div>
                    <div className="col-lg-7 mb-4">

                        <label className="form-label">
                            Referred By / Consultant
                        </label>

                        <input
                            type="text"
                            className="form-control"
                            name="refconsultant"
                            placeholder="Enter referring doctor / consultant name"
                            value={registerFormData.refconsultant}
                            onChange={handleRegisterInput}
                        />

                        <small className="text-muted">
                            This will be saved in Patient.refconsultant
                        </small>

                    </div>
                </div>


                {/* =================================================
                            APPOINTMENT
                        ================================================= */}

                <div
                    className="
                                erp-form-section
                                mt-4
                            "
                >

                    <i
                        className="
                                    fa-solid
                                    fa-calendar-days
                                    me-2
                                "
                    ></i>

                    Appointment Schedule

                </div>


                <div className="row">

                    <div className="col-lg-6 mb-4">

                        <label
                            className="form-label"
                        >

                            Appointment Date

                        </label>


                        <input

                            type="date"

                            className="
                                        form-control
                                    "

                            value={appointmentDate}

                            onChange={event => setAppointmentDate(event.target.value)}

                        />

                    </div>


                    <div className="col-lg-6 mb-4">

                        <label
                            className="form-label"
                        >

                            Appointment Time

                        </label>


                        <input

                            type="time"

                            className="
                                        form-control
                                    "

                            value={appointmentTime}

                            onChange={event => setAppointmentTime(event.target.value)}

                        />

                    </div>

                </div>


                {/* =================================================
                            SUBMIT
                        ================================================= */}

                <div className="text-end mt-4">

                    <button

                        type="button"

                        className="
                                    btn
                                    btn-success
                                    px-5
                                "

                        disabled={!newPatientValid || saving}

                        onClick={handleNewPatient}

                    >

                        {saving ? (<>

                            <i
                                className="
                                                fa-solid
                                                fa-spinner
                                                fa-spin
                                                me-2
                                            "
                            ></i>

                            Processing...

                        </>) : (<>

                            <i
                                className="
                                                fa-solid
                                                fa-user-check
                                                me-2
                                            "
                            ></i>

                            Register & Fix Appointment

                        </>)}

                    </button>

                </div>

            </div>

        </div>)}

    </section>);
}
export default FixAppointment;